import React, { useContext, useRef } from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import PropTypes from 'prop-types'
import AvatarBlank from '../../../../assets/images/avatarBlank.jpeg'
//@MUI
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import Typography from '@mui/material/Typography'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import moment from 'moment'
import { GlobalContext, UserContext } from '../../../../App'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import { Autocomplete, Avatar, Button, Chip, Tooltip } from '@mui/material'
import imageConverter from '../../../../helpers/imageConverter'
import { regionLabels } from '../../../../helpers/region'
import API from '../../../../services/API'
import { EmployeeFormContext } from './EmployeeForm'
import AnDatePicker from '../../../../components/DateTime/DatePicker'

const plateSx = {
  bgcolor: '#FFD400', // plate yellow
  color: '#111',
  border: '2px solid #111',
  borderRadius: '6px',
  fontWeight: 800,
  letterSpacing: '0.08em',
  fontFamily: `"UKNumberPlate", "Charles Wright", "Arial Black", Arial, sans-serif`,
  px: 1.2,
  height: 34,
  '& .MuiChip-label': {
    px: 0.5,
    textTransform: 'uppercase',
  },
  '& .MuiChip-deleteIcon': {
    color: '#111',
    '&:hover': { color: '#000' },
  },
}

const PersonalDetails = ({
  formValues,
  setFormValues,
  isSelf,
  onImageChange,
  isNew,
}) => {
  const { user } = useContext(UserContext)
  const imageRef = useRef()
  const { setToast, getUserAccount, tenantInfo } = useContext(GlobalContext)
  const handleImageChange = async (e) => {
    try {
      const file = e.target.files[0]
      const image = await imageConverter(file)
      if (isNew) {
        setFormValues((prev) => ({ ...prev, photograph: image }))
      } else {
        API.put(isSelf ? '/account/me' : '/employee', {
          photograph: image,
          ...(!isSelf && { _id: formValues._id }),
        }).then((res) => {
          if (res.status === 200) {
            isSelf && getUserAccount()
            // TODO: getUserAccount is only being used here, nowhere else
            onImageChange && onImageChange()
            setFormValues((prev) => ({ ...prev, photograph: image }))
            setToast({ message: 'Image uploaded successfully' })
          } else if (res.status === 413) {
            setToast({
              message: 'File size too large. Please upload a smaller image.',
              severity: 'error',
            })
          }
        })
      }
    } catch (e) {
      setToast({
        message: 'Unable to upload image. Please try again later.',
        severity: 'error',
      })
      console.log(e)
    }
  }

  return (
    <Paper sx={{ padding: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={9}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="title">Personal Details</Typography>
            </Grid>
            <Grid item xs={4} md={3} lg={2} xl={2}>
              <TextField
                label="Prefix"
                id="prefix"
                fullWidth
                autoComplete="nope"
                value={formValues.prefix ?? ''}
                onChange={(e) =>
                  setFormValues((prev) => ({
                    ...prev,
                    prefix: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={8} md={9} lg={5} xl={3.5}>
              <TextField
                label="First Name"
                id="first-name"
                fullWidth
                required
                autoComplete="nope"
                disabled={!user.employee && !!user.employeeApplication}
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    firstName: event.target.value,
                  }))
                }
                value={formValues?.firstName ?? ''}
              />
            </Grid>
            <Grid item xs={12} md={5} xl={3.5}>
              <TextField
                label="Last Name"
                id="last-name"
                fullWidth
                disabled={!user.employee && !!user.employeeApplication}
                required
                autoComplete="nope"
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    lastName: event.target.value,
                  }))
                }
                value={formValues.lastName ?? ''}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <FormControl fullWidth>
                <AnDatePicker
                  disableFuture
                  label="Date of Birth"
                  value={
                    formValues?.employee?.personnelInfo?.dob
                      ? moment(formValues.employee.personnelInfo.dob)
                      : null
                  }
                  onChange={(newDate) =>
                    setFormValues((prev) => ({
                      ...prev,
                      employee: {
                        ...prev.employee,
                        personnelInfo: {
                          ...prev.employee?.personnelInfo,
                          dob: newDate,
                        },
                      },
                    }))
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth>
                <InputLabel id="pronouns-label">Pronouns</InputLabel>
                <Select
                  labelId="pronouns-label"
                  id="pronouns"
                  value={formValues.employee?.personnelInfo?.pronouns || ''}
                  label="pronouns"
                  onChange={(event) => {
                    setFormValues((prev) => ({
                      ...prev,
                      employee: {
                        ...prev.employee,
                        personnelInfo: {
                          ...prev.employee?.personnelInfo,
                          pronouns: event.target.value,
                        },
                      },
                    }))
                  }}
                >
                  <MenuItem value="She/Her/Hers">She/Her/Hers</MenuItem>
                  <MenuItem value="He/Him/His">He/Him/His</MenuItem>
                  <MenuItem value="They/Them/Theirs">They/Them/Theirs</MenuItem>
                  <MenuItem value="Prefer Not To Say">
                    Prefer not to say
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                fullWidth
                label={regionLabels(tenantInfo?.region).niNumberLabel}
                id="ni-number"
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    employee: {
                      ...prev.employee,
                      personnelInfo: {
                        ...prev.employee?.personnelInfo,
                        nationalInsurance: event.target.value.toUpperCase(),
                      },
                    },
                  }))
                }
                value={
                  formValues.employee?.personnelInfo?.nationalInsurance ?? ''
                }
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                fullWidth
                label="Nationality"
                id="nationality"
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    employee: {
                      ...prev.employee,
                      personnelInfo: {
                        ...prev.employee?.personnelInfo,
                        nationality: event.target.value,
                      },
                    },
                  }))
                }
                value={formValues.employee?.personnelInfo?.nationality ?? ''}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                fullWidth
                label="Birthplace"
                id="birthplace"
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    employee: {
                      ...prev.employee,
                      personnelInfo: {
                        ...prev.employee?.personnelInfo,
                        birthplace: event.target.value,
                      },
                    },
                  }))
                }
                value={formValues.employee?.personnelInfo?.birthplace ?? ''}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          lg={3}
          alignItems="center"
          justifyContent="center"
          // rowGap={2}
          display="flex"
          flexDirection="column"
          sx={{ cursor: 'pointer' }}
        >
          <Avatar
            src={formValues.photograph}
            sx={{
              height: 200,
              width: 200,
              borderRadius: '5px 5px 0 0',
            }}
          />
          <input
            ref={imageRef}
            type="file"
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleImageChange}
          />
          <Button
            sx={{ width: 200, borderRadius: '0px 0px 5px 5px' }}
            onClick={() => imageRef.current.click()}
            variant="contained"
          >
            Change ID Photo
          </Button>
        </Grid>
        {!isSelf && (
          <Grid item xs={12} lg={6}>
            <TextField
              label="Staff ID / PIN"
              value={formValues.employee?.pinNumber ?? ''}
              autoComplete="off"
              onChange={(e) =>
                setFormValues((prev) => ({
                  ...prev,
                  employee: {
                    ...prev.employee,
                    pinNumber: e.target.value,
                  },
                }))
              }
              fullWidth
            />
          </Grid>
        )}
        <Grid item xs={12} lg={6}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            filterOptions={(x) => x}
            open={false}
            value={
              formValues?.employee?.personnelInfo?.vehicleRegistrations ?? []
            }
            onChange={(e, newValue) =>
              setFormValues((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    vehicleRegistrations: newValue,
                  },
                },
              }))
            }
            // selected plates look like yellow plates
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((plate, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={`${plate}-${index}`}
                  label={String(plate).toUpperCase()}
                  sx={{
                    '& .MuiChip-label': {
                      px: 0.5,
                      textTransform: 'uppercase',
                    },
                    '& .MuiChip-deleteIcon': {
                      color: '#111',
                      ml: 1,
                      '&:hover': { color: '#000' },
                    },
                    bgcolor: '#FFD307', // plate yellow
                    color: '#111',
                    // border: "2px solid #111",
                    borderRadius: 1,
                    // fontWeight: 800,
                    letterSpacing: '0.08em',
                    fontFamily: `"UKnumberplate", "Charles Wright", "Arial Black", Arial, sans-serif`,
                    px: 1.2,
                    height: 34,
                  }}
                  variant="filled"
                />
              ))
            }
            // dropdown options also look like plates
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ py: 0.75 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    ...plateSx,
                    height: 30,
                    px: 1,
                    borderRadius: '6px',
                  }}
                >
                  <Typography sx={{ fontWeight: 800, letterSpacing: '0.08em' }}>
                    {String(option).toUpperCase()}
                  </Typography>
                </Box>
              </Box>
            )}
            autoComplete="off"
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vehicle Registrations"
                helperText="Employee's own vehicles. Press 'Enter' to add."
              />
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  )
}

PersonalDetails.propTypes = {
  isSelf: PropTypes.bool.isRequired,
}

export default PersonalDetails
